import { AdjustIcon } from "@components/Icons";
import queryKeys from "@configs/react-query/query-key";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useMutationData } from "@hooks/useMutationData";
import { useTheme } from "@hooks/useTheme";
import { formatDateTime, formatDate, formatVerificationType } from "@utils/common";
import UserNameWithId from "@components/UI/UserNameWithId";
import { useEffect, useRef, useState, useCallback } from "react";
import AdvancedSearch from "../Search/AdvancedSearch";
import { useAdvancedSearch } from "@hooks/useAdvancedSearch";
import { getEffectiveQuickSearchValue } from "@utils/searchHelpers";
import TableWithPagination from "../Tables/TableWithPagination";
import { OutlineButton, PrimaryButton } from "@components/Buttons";
import { useTranslation } from "react-i18next";
import { useSearchInfo } from "@hooks/useSearchInfo";
import { SCAN_TYPE_OPTIONS } from "@constants/index";
import { ITEMS_PER_PAGE_KEY, DEFAULT_ITEMS_PER_PAGE } from "@constants/table";
import useItemsPerPage from "@hooks/useItemsPerPage";
import useEscapeKey from "@hooks/useEscapeKey";
import { useVerificationTypeOptions } from "@hooks/useVerificationTypeOptions";

const SelectVerificationFilesForSbomMergeModal = ({
  isOpen,
  onClose,
  onAdd,
  initialSelectedFiles = [],
  "data-testid": dataTestId = "select-verification-files-sbom-merge-modal",
}) => {
  const fieldInputTestId = (name) => `${dataTestId}-field-${name}-input`;
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [verificationFiles, setVerificationFiles] = useState([]);
  const [totalFiles, setTotalFiles] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedFiles, setSelectedFiles] = useState(initialSelectedFiles);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const debounceTimerRef = useRef(null);
  const isInitialMount = useRef(true);
  const { creatorList, fetchSearchInfo, scanToolList } = useSearchInfo();
  const { itemsPerPage, handleItemsPerPageChange } = useItemsPerPage(ITEMS_PER_PAGE_KEY.ADD_VERIFICATION_SBOM_MERGE_LIST, DEFAULT_ITEMS_PER_PAGE);

  const rowKey = ["projectId", "verificationVersion", "verificationType"];
  const { verificationTypeOptions } = useVerificationTypeOptions({
    includeSBOM: true,
    scanTypeOptionsList: SCAN_TYPE_OPTIONS,
  });
  const advancedSearchFields = [
    {
      name: "projectId",
      label: t("project.project-id"),
      type: "text",
      placeholder: t("project.project-id"),
    },
    {
      name: "verificationType",
      label: t("project.verification-type"),
      type: "select",
      placeholder: t("project.verification-type"),
      options: verificationTypeOptions,
    },
    {
      name: "createrId",
      label: t("project.creator"),
      type: "select",
      placeholder: t("project.creator"),
      options: creatorList,
      displayFormatLabelWithID: true,
    },
    {
      name: "createdAtFrom",
      type: "date",
      placeholder: t("project.creation-date-from"),
    },
    {
      name: "createdAtTo",
      type: "date",
      placeholder: t("project.creation-date-to"),
    },
  ];

  const getProjectVerificationData = useMutationData(
    queryKeys.getProjectVerificationData,
    {
      method: "POST",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          // Ensure consistent data structure for table rows
          const processedFiles = (response.object || []).map(file => ({
            ...file,
            verificationVersion: file.verificationVersion,
            projectId: file.projectId || null
          }));
          setVerificationFiles(processedFiles);
          setTotalFiles(response.objectCount || 0);
          setTotalPages(Math.ceil((response.objectCount || 0) / itemsPerPage));
        }
        setLoading(false);
      },
      onError: (error) => {
        console.error("Error during get data:", error);
        setLoading(false);
      },
    },
  );

  const { showAdvancedSearch, setShowAdvancedSearch, handleAdvancedSearchToggle } = useAdvancedSearch({
    searchFields: advancedSearchFields,
    onPanelClose: () => {
      setSearchFilters({});
    },
  });

  const fetchFiles = useCallback((page, searchValue, filters) => {
    setLoading(true);
    const startRow = (page - 1) * itemsPerPage;
    const formattedFilters = { ...filters };
    if (formattedFilters?.createdAtFrom) {
      formattedFilters.createdAtFrom = formatDate(formattedFilters.createdAtFrom);
    }
    if (formattedFilters?.createdAtTo) {
      formattedFilters.createdAtTo = formatDate(formattedFilters.createdAtTo);
    }
    const { projectName: advancedProjectName, ...restFilters } = formattedFilters;
    const quickProjectName = getEffectiveQuickSearchValue(searchValue, {
      isAdvancedPanelOpen: showAdvancedSearch,
    });
    const projectName = showAdvancedSearch ? advancedProjectName : quickProjectName;
    getProjectVerificationData.mutate({
      ...restFilters,
      ...(projectName ? { projectName } : {}),
      startRow,
      pageSize: itemsPerPage,
    });
  }, [itemsPerPage, getProjectVerificationData, showAdvancedSearch]);

  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setSearchFilters({});
      setShowAdvancedSearch(false);
      setCurrentPage(1);
      fetchSearchInfo();
      setSelectedFiles(initialSelectedFiles);
      setSelectedRowIds(initialSelectedFiles.map(file => ({
        projectId: file.projectId || null,
        verificationVersion: file.verificationVersion,
        verificationType: file.verificationType
      })));
      fetchFiles(1, "", {});
      isInitialMount.current = true;
    }
    // eslint-disable-next-line
  }, [isOpen, initialSelectedFiles]);

  // Debounce search
  useEffect(() => {
    if (!isOpen) return;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setIsSearching(true);
    debounceTimerRef.current = setTimeout(() => {
      setCurrentPage(1);
      fetchFiles(1, search, searchFilters);
      setIsSearching(false);
    }, 600);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line
  }, [search]);

  useEffect(() => {
    if (!isOpen) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip first render
    }
    fetchFiles(currentPage, search, searchFilters);
  }, [currentPage, itemsPerPage, showAdvancedSearch, searchFilters, isInitialMount]);

  useEffect(() => {
    setSelectedRowIds(selectedFiles.map(file => ({
      projectId: file.projectId || null,
      verificationVersion: file.verificationVersion,
      verificationType: file.verificationType
    })));
  }, [selectedFiles]);

  useEscapeKey(onClose, isOpen);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const handleAdvancedSearch = (fields) => {
    setSearchFilters(fields);
    setCurrentPage(1);
    fetchFiles(1, search, fields);
  };

  const handleItemsPerPageChangeWithReset = (newItemsPerPage) => {
    handleItemsPerPageChange(newItemsPerPage);
    setCurrentPage(1); // Reset to page 1 when changing number of rows
  };

  const handleCheckboxChange = (selectedRows) => {
    // Get the current page's file objects that are selected
    const currentPageSelectedFiles = verificationFiles.filter(file => {
      return selectedRows.some(selectedRow =>
        selectedRow.projectId === (file.projectId || null) &&
        selectedRow.verificationVersion === file.verificationVersion &&
        selectedRow.verificationType === file.verificationType
      );
    });

    // Get the current page's file objects that are NOT selected
    const currentPageUnselectedFiles = verificationFiles.filter(file => {
      return !selectedRows.some(selectedRow =>
        selectedRow.projectId === (file.projectId || null) &&
        selectedRow.verificationVersion === file.verificationVersion &&
        selectedRow.verificationType === file.verificationType
      );
    });

    // Remove any files from current page that are no longer selected
    const updatedSelectedFiles = selectedFiles.filter(selectedFile => {
      return !currentPageUnselectedFiles.some(unselectedFile =>
        unselectedFile.projectId === (selectedFile.projectId || null) &&
        unselectedFile.verificationVersion === selectedFile.verificationVersion &&
        unselectedFile.verificationType === selectedFile.verificationType
      );
    });

    // Add any new files from current page that are now selected
    const newSelectedFiles = currentPageSelectedFiles.filter(newFile => {
      return !updatedSelectedFiles.some(existingFile =>
        existingFile.projectId === (newFile.projectId || null) &&
        existingFile.verificationVersion === newFile.verificationVersion &&
        existingFile.verificationType === newFile.verificationType
      );
    });

    setSelectedFiles([...updatedSelectedFiles, ...newSelectedFiles]);
  };

  const handleAdd = () => {
    onAdd(selectedFiles);
  };

  const handleRowClick = (row) => {

    const isCurrentlySelected = selectedFiles.some(file =>
      file.projectId === row.projectId &&
      file.verificationVersion === row.verificationVersion &&
      file.verificationType === row.verificationType
    );

    if (isCurrentlySelected) {
      // Remove from selection
      setSelectedFiles(selectedFiles.filter(file =>
        !(file.projectId === row.projectId &&
          file.verificationVersion === row.verificationVersion &&
          file.verificationType === row.verificationType)
      ));
    } else {
      // Add to selection
      setSelectedFiles([...selectedFiles, row]);
    }
  };

  // Table columns and config
  const columns = [
    "verificationVersion",
    "realFileName",
    "verificationType",
    "projectId",
    "projectName",
    "createrName",
    "createdAt",
  ];

  const columnConfig = {
    verificationVersion: {
      title: t("common.version"),
      style: { textAlign: "center" },
    },
    realFileName: {
      title: t("project.verification.verification-file-name"),
      style: { textAlign: "left" },
    },
    verificationType: {
      title: t("project.verification.verification-type"),
      customRender: (value) => formatVerificationType(value),
      style: { textAlign: "left" },
    },
    projectId: {
      title: t("project.project-id"),
      style: { textAlign: "left" },
      customRender: (value) => value || "-",
    },
    projectName: {
      title: t("project.project-name"),
      style: { textAlign: "left" },
      customRender: (value) => value || "-",
    },
    createrName: {
      title: t("project.creator"),
      style: { textAlign: "left" },
      customRender: (value, row) => <UserNameWithId name={row.createrName} id={row.createrId} />,
    },
    createdAt: {
      title: t("project.creation-date"),
      customRender: (value) => formatDateTime(value),
    },
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid={`${dataTestId}-overlay`}
      className={`fixed inset-0 ${colors.bgBlurModal} z-50 flex items-center justify-start overflow-x-auto p-4 backdrop-blur-[1px]`}
    >
      <div
        data-testid={dataTestId}
        className={`${colors.bgModal} mx-auto w-[1000px] min-w-[1000px] max-h-[95vh] flex-shrink-0 overflow-auto rounded-lg shadow-xl`}
      >
        {/* Header */}
        <div className={`px-6 py-4`} data-testid={`${dataTestId}-header`}>
          <div className="flex items-center justify-between">
            <h2 className={`${colors.textPrimary} text-xl font-bold uppercase`} data-testid={`${dataTestId}-title`}>
              {t("project.verification.select-verification-project-versions")}
            </h2>
          </div>
          <p className={`text-sm ${colors.textSecondary} mt-2`}>
            {t("project.verification.select-verification-project-version")}
          </p>
        </div>

        {/* Search Section */}
        <div className="space-y-4 px-6 pb-4">
          <div className="flex items-center gap-4 max-w-xl">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon
                className={`absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform ${colors.textSecondary}`}
              />
              <input
                type="text"
                placeholder={t("project.verification.search-by-project-name")}
                value={search}
                onChange={handleSearch}
                className={`w-full rounded-lg border px-3 py-2 pr-8 pl-10 text-sm focus:outline-none ${colors.bgInput} ${colors.borderPrimary} ${colors.textPrimary} ${colors.textPlaceholder} ${colors.borderHoverPrimary}`}
                data-testid={fieldInputTestId("search")}
              />
              {isSearching && (
                <div className="absolute top-1/2 right-3 -translate-y-1/2 transform">
                  <div className={`h-3 w-3 animate-spin rounded-full border-b-2 ${colors.borderLoader}`}></div>
                </div>
              )}
            </div>
            <button
              onClick={handleAdvancedSearchToggle}
              className={`inline-flex items-center justify-center border px-4 py-2 ${colors.borderOutLineButton} rounded-md text-sm font-medium shadow-sm ${colors.textActive} ${colors.hoverOutlineModalSelection} whitespace-nowrap`}
              data-testid={fieldInputTestId("advanced-search-toggle")}
            >
              <AdjustIcon className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">{t("project.advancedSearch")}</span>
            </button>
          </div>
        </div>

        {/* Advanced Search */}
        {showAdvancedSearch && (
          <div className="relative mx-6 mb-4 z-21">
            <AdvancedSearch
              title={t("project.advancedSearch")}
              searchFields={advancedSearchFields}
              searchHandler={handleAdvancedSearch}
              withoutSearch={true}
              gridCols="md:grid-cols-3 xl:grid-cols-4"
              padding="p-4"
            />
          </div>
        )}

        {/* TableWithPagination */}
        <div className="mb-4 px-6" style={{ overflow: "hidden" }} data-testid={`${dataTestId}-table`}>
          <TableWithPagination
            columns={columns}
            columnConfig={columnConfig}
            data={verificationFiles}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={loading}
            rowKey={rowKey}
            totalPages={totalPages}
            totalData={totalFiles}
            maxRowsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChangeWithReset}
            hasCheckbox={true}
            selectedRows={selectedRowIds}
            onCheckboxChange={handleCheckboxChange}
            onRowClick={handleRowClick}
            showPagination={true}
            scrollY={showAdvancedSearch ? "300px" : "550px"}
            paddingYHeader="py-2"
            stickyHeader={true}
            showItemsPerPageSelector={true}
          />
        </div>

        {/* Footer */}
        <div className={`px-6 py-4 ${colors.borderPrimary}`} data-testid={`${dataTestId}-footer`}>
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <OutlineButton data-testid={`${dataTestId}-cancel-button`} onClick={onClose}>
                {t("common.cancel-button")}
              </OutlineButton>
              <PrimaryButton
                data-testid={`${dataTestId}-add-button`}
                onClick={handleAdd}
                disabled={!selectedFiles.length}
              >
                {t("common.add-button")}
                {` (${selectedFiles.length})`}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectVerificationFilesForSbomMergeModal; 
